import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Field } from '@mailflow/ui';
import { useCreateContact, listsQuery } from '../lib/contacts.js';
import { ApiRequestError } from '../lib/api.js';

/**
 * Dialog zum Anlegen eines Kontakts.
 *
 * Modal mit Fokusfalle und Escape zum Schliessen — ohne beides ist ein Dialog
 * per Tastatur nicht bedienbar und der Fokus wandert hinter das Overlay.
 */
export function NewContactDialog({ onClose }: { onClose: () => void }) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const create = useCreateContact();
  const lists = useQuery(listsQuery);

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [listId, setListId] = useState('');

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector<HTMLInputElement>('input')?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab') return;

      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, input, select, textarea, a[href]',
      );
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previous?.focus();
    };
  }, [onClose]);

  const fieldErrors = create.error instanceof ApiRequestError ? (create.error.fields ?? {}) : {};
  const generalError =
    create.error instanceof ApiRequestError && Object.keys(fieldErrors).length === 0
      ? create.error.message
      : null;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      await create.mutateAsync({
        email,
        firstName: firstName || null,
        lastName: lastName || null,
        ...(listId ? { listIds: [listId] } : {}),
      });
      onClose();
    } catch {
      // Fehler steht in create.error und wird angezeigt.
    }
  }

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="card dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-contact-title"
        ref={dialogRef}
      >
        <div className="card__body stack">
          <h2 id="new-contact-title" style={{ fontSize: 'var(--text-lg)' }}>
            Kontakt anlegen
          </h2>

          {generalError && (
            <div className="alert" data-tone="danger" role="alert">
              {generalError}
            </div>
          )}

          <form className="stack" onSubmit={handleSubmit} noValidate>
            <Field
              label="E-Mail-Adresse"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              {...(fieldErrors.email ? { error: fieldErrors.email } : {})}
            />
            <div className="grid-2">
              <Field
                label="Vorname"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              <Field
                label="Nachname"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="new-contact-list">
                Zu Liste hinzufügen
              </label>
              <select
                id="new-contact-list"
                className="input"
                value={listId}
                onChange={(e) => setListId(e.target.value)}
              >
                <option value="">Keine Liste</option>
                {lists.data?.items.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="dialog__actions">
              <Button variant="secondary" onClick={onClose}>
                Abbrechen
              </Button>
              <Button type="submit" loading={create.isPending}>
                Anlegen
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
